if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.error = false;
      }

      /**
       * @returns {string} Path beginning with `/` (required for Cart API `sections_url`)
       */
      sectionsUrlPath() {
        const p = window.location.pathname || '/';
        return p.startsWith('/') ? p : `/${p}`;
      }

      hasRenderableBundledSections(response) {
        const sections = response && response.sections;
        if (!sections || typeof sections !== 'object') return false;
        return Object.values(sections).some(
          (html) => html != null && String(html).trim() !== ''
        );
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.error = false;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const formData = new FormData(this.form);
        const variantId = this.resolveVariantId();
        if (variantId) {
          formData.set('id', variantId);
          const idField = this.variantIdInput;
          if (idField) {
            idField.value = variantId;
            idField.removeAttribute('disabled');
          }
        }
        if (this.cart) {
          formData.set(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id).join(',')
          );
          formData.set('sections_url', this.sectionsUrlPath());
          this.cart.setActiveElement(document.activeElement);
        }

        fetch(getCartAddJsUrl(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: formData,
        })
          .then(async (response) => {
            let data = {};
            try {
              data = await response.json();
            } catch (parseErr) {
              console.error(parseErr);
            }

            if (!response.ok) {
              this.handleErrorMessage(
                data.description ||
                  data.message ||
                  window.cartStrings?.error ||
                  ''
              );
              return;
            }

            if (data.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: this.form.querySelector('[name="id"]')?.value,
                errors: data.errors || data.description,
                message: data.message,
              });
              this.handleErrorMessage(data.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: this.form.querySelector('[name="id"]')?.value,
                cartData: data,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      try {
                        this.#afterAddUpdateDrawer(data);
                      } catch (afterAddErr) {
                        console.error('[product-form] after add / drawer update', afterAddErr);
                      }
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                try {
                  this.#afterAddUpdateDrawer(data);
                } catch (afterAddErr) {
                  console.error('[product-form] after add / drawer update', afterAddErr);
                }
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings?.error || '');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            if (spinner) spinner.classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      /** @param {Record<string, unknown>} cartAddResponse */
      #afterAddUpdateDrawer(cartAddResponse) {
        if (!this.cart || this.cart.tagName !== 'CART-DRAWER') {
          if (this.cart && typeof this.cart.renderContents === 'function') {
            this.cart.renderContents(cartAddResponse);
          }
          return;
        }

        if (this.hasRenderableBundledSections(cartAddResponse)) {
          this.cart.renderContents(cartAddResponse);
          return;
        }

        this.cart.refreshContentsFromCartPage().then(() => {
          this.cart.open();
        });
      }

      localizeCartErrorMessage(errorMessage) {
        if (!errorMessage || typeof errorMessage !== 'string') return errorMessage;
        if (/already sold out/i.test(errorMessage)) {
          return window.variantStrings?.soldOut || errorMessage;
        }
        return errorMessage;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        const displayMessage = errorMessage
          ? this.localizeCartErrorMessage(errorMessage)
          : false;

        this.errorMessageWrapper.toggleAttribute('hidden', !displayMessage);

        if (displayMessage) {
          this.errorMessage.textContent = displayMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      resolveVariantId() {
        const productInfo = this.closest('product-info');
        if (productInfo) {
          try {
            const raw = productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent;
            if (raw?.trim()) {
              const variant = JSON.parse(raw.trim());
              if (variant?.id) return String(variant.id);
            }
          } catch (e) {
            console.warn('[product-form] resolveVariantId', e);
          }
        }

        const input = this.variantIdInput;
        const fromInput = input?.value ? String(input.value).trim() : '';
        if (fromInput) return fromInput;

        const checked = productInfo?.querySelector(
          'variant-selects input[data-variant-id]:checked'
        );
        if (checked?.dataset?.variantId) {
          return String(checked.dataset.variantId);
        }

        return '';
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
